import { useDomainFromUrl } from '@/app/_common/domain/use-domain-from-url'
import { Turn, turnStatusKeyFactory, useFetchTurnStatus } from '@/hooks/api/use-turn-status-query'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { isTurnStatusLoading } from './plan-steps/step-status'
import { chatDataKeyFactory, type ChatData } from './use-chat-data-query'
import type { ChatTurnEntry } from './use-turn-parsed'

function useChatQueriesHelpers({
  workflowId,
  setPlaceholder,
}: {
  workflowId: string
  setPlaceholder: (turnId: string, text: string) => void
}) {
  const qc = useQueryClient()
  const { domainId } = useDomainFromUrl()
  const chatDataQueryKey = chatDataKeyFactory({ workflowId, domainId })
  const fetchTurnStatus = useFetchTurnStatus()

  const getTurnQueryKey = (turnId: string) => turnStatusKeyFactory({ workflowId, turnId, domainId })

  const getChatData = React.useCallback(
    () => qc.getQueryData<ChatData>(chatDataQueryKey),
    [qc, chatDataQueryKey],
  )
  const setChatData = React.useCallback(
    (chatData: ChatData) => {
      qc.setQueryData<ChatData>(chatDataQueryKey, chatData)
    },
    [qc, chatDataQueryKey],
  )

  const getTurnData = React.useCallback(
    (turnId: string) => qc.getQueryData<Turn>(getTurnQueryKey(turnId)),
    [qc, getTurnQueryKey],
  )

  /**
   * Updates a turn in chat data and manages activeTurnId
   */
  const updateTurnInChatData = React.useCallback(
    (updatedTurn: Turn) => {
      const chatData = getChatData()
      if (!chatData) return

      // Check if we've already processed this exact turn data to prevent infinite loops
      const existingTurn = chatData.turns.find(
        (entry) => entry.type === 'turn' && entry.turnId === updatedTurn.turnId,
      )

      if (existingTurn && existingTurn.type === 'turn') {
        // Compare the status to see if anything actually changed
        if (existingTurn.turn.status === updatedTurn.status) {
          return // No change, don't update
        }
      }

      const updatedTurns = chatData.turns.map((entry) =>
        entry.type === 'turn' && entry.turnId === updatedTurn.turnId
          ? { ...entry, turn: updatedTurn }
          : entry,
      )

      // Determine the new activeTurnId
      let newActiveTurnId: string | null = null

      // Find the last turn that's still loading
      for (let i = updatedTurns.length - 1; i >= 0; i--) {
        const entry = updatedTurns[i]
        if (entry.type === 'turn' && isTurnStatusLoading(entry.turn.status)) {
          newActiveTurnId = entry.turnId
          break
        }
      }

      // Only update if something actually changed
      const hasChanges =
        newActiveTurnId !== chatData.activeTurnId ||
        JSON.stringify(updatedTurns) !== JSON.stringify(chatData.turns)

      if (hasChanges) {
        // Defer the update to prevent React error #185
        setTimeout(() => {
          setChatData({
            ...chatData,
            turns: updatedTurns,
            activeTurnId: newActiveTurnId,
          })
        }, 0)
      }
    },
    [getChatData, setChatData],
  )

  /**
   * Updates turn data and syncs with chat data, including activeTurnId management
   */
  const setTurnData = React.useCallback(
    (turn: Turn) => {
      // Update turn cache
      qc.setQueryData<Turn>(getTurnQueryKey(turn.turnId), turn)

      // Update turn in chat data and manage activeTurnId
      updateTurnInChatData(turn)
    },
    [qc, getTurnQueryKey, updateTurnInChatData],
  )

  /**
   * Syncs chat data with turn cache and updates activeTurnId
   */
  const syncChatDataFromTurnsCache = React.useCallback(() => {
    const chatData = getChatData()
    if (!chatData) return

    const updatedTurns = chatData.turns.map((entry) => {
      if (entry.type !== 'turn') return entry
      const turn = getTurnData(entry.turnId)
      return turn ? { ...entry, turn } : entry
    })

    // Determine the new activeTurnId
    let newActiveTurnId: string | null = null

    // Find the last turn that's still loading
    for (let i = updatedTurns.length - 1; i >= 0; i--) {
      const entry = updatedTurns[i]
      if (entry.type === 'turn' && isTurnStatusLoading(entry.turn.status)) {
        newActiveTurnId = entry.turnId
        break
      }
    }

    // Only update if something actually changed
    const hasChanges =
      newActiveTurnId !== chatData.activeTurnId ||
      JSON.stringify(updatedTurns) !== JSON.stringify(chatData.turns)

    if (hasChanges) {
      // Defer the update to prevent React error #185
      setTimeout(() => {
        setChatData({
          ...chatData,
          turns: updatedTurns,
          activeTurnId: newActiveTurnId,
        })
      }, 0)
    }
  }, [getChatData, getTurnData, setChatData])

  /**
   * Refetches all data and syncs state
   */
  const refetchAll = React.useCallback(async () => {
    await qc.refetchQueries({
      queryKey: chatDataQueryKey,
    })

    const chat = getChatData()
    if (!chat) return null

    // Refetch all turns in parallel
    const turnPromises = chat.turns
      .filter((entry): entry is ChatTurnEntry & { type: 'turn' } => entry.type === 'turn')
      .map((entry) =>
        qc.fetchQuery<Turn>({
          queryKey: getTurnQueryKey(entry.turnId),
          queryFn: () => fetchTurnStatus({ workflowId, turnId: entry.turnId }),
        }),
      )

    await Promise.all(turnPromises)
    syncChatDataFromTurnsCache()
    return getChatData()
  }, [qc, chatDataQueryKey, workflowId, fetchTurnStatus])

  /**
   * Refetches a specific turn and updates chat data
   */
  const refetchTurn = React.useCallback(
    async (turnId: string) => {
      const turn = await qc.ensureQueryData<Turn>({
        queryKey: getTurnQueryKey(turnId),
        queryFn: () => fetchTurnStatus({ workflowId, turnId }),
      })

      // Update chat data with the new turn data
      updateTurnInChatData(turn)

      return turn
    },
    [qc, getTurnQueryKey, workflowId, fetchTurnStatus, updateTurnInChatData],
  )

  /**
   * Cancels ongoing queries
   */
  const cancelQueries = React.useCallback(
    (turnId?: string) => {
      const tasks = [qc.cancelQueries({ queryKey: chatDataQueryKey })]
      if (turnId) tasks.push(qc.cancelQueries({ queryKey: getTurnQueryKey(turnId) }))
      return Promise.all(tasks)
    },
    [qc, chatDataQueryKey],
  )

  /**
   * Invalidates chat data
   */
  const invalidateChat = React.useCallback(
    () => qc.invalidateQueries({ queryKey: chatDataQueryKey }),
    [qc, chatDataQueryKey],
  )

  /**
   * Adds a placeholder turn to the chat
   */
  const addPlaceholderTurn = React.useCallback(
    async (
      placeholderPrompt: string,
      files?: FileList | null,
    ): Promise<{ previous?: ChatData }> => {
      const prev = getChatData()
      const placeholderId = `placeholder-${crypto.randomUUID()}`

      // Store placeholder text in state immediately for instant access
      setPlaceholder(placeholderId, placeholderPrompt)

      if (prev) {
        const placeholder: ChatTurnEntry = {
          turnId: placeholderId,
          type: 'placeholder',
          placeholderPrompt,
          messages: [],
        }

        setChatData({
          ...prev,
          turns: [...prev.turns, placeholder],
          // Set activeTurnId to null since placeholder is not a real turn
          activeTurnId: null,
        })
      }

      return { previous: prev }
    },
    [getChatData, setChatData, setPlaceholder],
  )

  return {
    cancelQueries,
    refetchAll,
    refetchTurn,
    invalidateChat,
    getChatData,
    setChatData,
    getTurnData,
    setTurnData,
    syncChatDataFromTurnsCache,
    updateTurnInChatData,
    addPlaceholderTurn,
  }
}

export { useChatQueriesHelpers }
